import { createHash } from "crypto";
import { buildDocumentCacheKey } from "@gencow/core";
import type {
  AuthCtx,
  DocumentConvertInput,
  DocumentConvertResult,
  DocumentResolvedProvider,
  DocumentVisibility,
  GencowServicesCtx,
  Storage,
  WorkflowDocumentServicesCtx,
} from "@gencow/core";
import { extractLocalDocument, resolveDocumentSourceKey } from "./document-convert-local.js";
import { resolveAllowExternalProvidersPolicy, type RagCorpusPolicyDbLike } from "./rag-corpus-policy.js";

const MAX_DOCUMENT_SIZE_BYTES = 50 * 1024 * 1024;
const DEFAULT_DOCUMENT_PROXY_PATH = "/convert";
const SUPPORTED_MIME_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/haansofthwp",
  "application/x-hwp",
  "application/vnd.hancom.hwp",
  "application/vnd.hancom.hwpx",
  "application/x-hwpx",
  "application/vnd.hancom.hwpml",
  "application/x-hwpml",
  "text/plain",
  "text/markdown",
  "text/html",
  "text/csv",
]);
const PLATFORM_AUTO_MIME_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/haansofthwp",
  "application/x-hwp",
  "application/vnd.hancom.hwp",
  "application/vnd.hancom.hwpx",
  "application/x-hwpx",
  "application/vnd.hancom.hwpml",
  "application/x-hwpml",
]);
const PLATFORM_AUTO_EXTENSIONS = new Set([".pdf", ".docx", ".xlsx", ".hwp", ".hwpx", ".hwpml"]);

const documentConvertCache = new Map<string, DocumentConvertResult>();

type DocumentConvertService = WorkflowDocumentServicesCtx["document"];
type InternalWorkflowFactory = (params: {
  workflowId: string;
  workflowName: string;
  auth: AuthCtx;
}) => WorkflowDocumentServicesCtx;
type ResolveExternalPolicy = (params: {
  corpus: string;
  visibility: DocumentVisibility;
  ownerUserId: string | null;
}) => Promise<boolean>;

export const WORKFLOW_DOCUMENT_SERVICE_FACTORY = Symbol.for("gencow.workflow.document.serviceFactory");

type ServerServicesWithFactory = GencowServicesCtx & {
  [WORKFLOW_DOCUMENT_SERVICE_FACTORY]?: InternalWorkflowFactory;
};

export class DocumentConvertRuntimeError extends Error {
  code: string;
  status: number;

  constructor(message: string, options: { code: string; status: number }) {
    super(message);
    this.name = "DocumentConvertRuntimeError";
    this.code = options.code;
    this.status = options.status;
  }
}

export type CreateDocumentConvertServiceOptions = {
  workflowId?: string | null;
  workflowName?: string;
  appName: string;
  storage: Storage;
  auth: AuthCtx;
  appToken?: string;
  documentProxyUrl?: string | null;
  allowPrivateExternalCorpora?: string[];
  resolveExternalPolicy?: ResolveExternalPolicy;
  fetchImpl?: typeof fetch;
  now?: () => number;
};

function cloneResult(result: DocumentConvertResult): DocumentConvertResult {
  return {
    text: result.text,
    markdown: result.markdown,
    pages: result.pages.map((page) => ({ ...page })),
    sections: result.sections.map((section) => ({ ...section, path: [...section.path] })),
    warnings: [...result.warnings],
    providerTrace: { ...result.providerTrace },
  };
}

function inferMimeTypeFromFilename(fallbackName: string): string {
  const loweredName = fallbackName.toLowerCase();
  if (loweredName.endsWith(".html") || loweredName.endsWith(".htm")) return "text/html";
  if (loweredName.endsWith(".csv")) return "text/csv";
  if (loweredName.endsWith(".md")) return "text/markdown";
  if (loweredName.endsWith(".txt")) return "text/plain";
  if (loweredName.endsWith(".pdf")) return "application/pdf";
  if (loweredName.endsWith(".docx"))
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (loweredName.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (loweredName.endsWith(".hwp")) return "application/haansofthwp";
  if (loweredName.endsWith(".hwpx")) return "application/vnd.hancom.hwpx";
  if (loweredName.endsWith(".hwpml")) return "application/vnd.hancom.hwpml";
  return "application/octet-stream";
}

function normalizeMimeType(value: string | undefined, fallbackName: string): string {
  const trimmed = value?.trim().toLowerCase().split(";")[0];
  if (!trimmed || trimmed === "application/octet-stream" || trimmed === "binary/octet-stream") {
    return inferMimeTypeFromFilename(fallbackName);
  }
  return trimmed;
}

function resolveOwnerUserId(input: DocumentConvertInput, auth: AuthCtx): string | null {
  if (input.visibility !== "private") {
    return input.ownerUserId?.trim() || null;
  }

  const user = auth.requireAuth();
  if (input.ownerUserId && input.ownerUserId !== user.id) {
    throw new DocumentConvertRuntimeError("ownerUserId must match the authenticated workflow owner", {
      code: "DOCUMENT_OWNER_MISMATCH",
      status: 403,
    });
  }
  return input.ownerUserId ?? user.id;
}

function ensureSupportedMimeType(mimeType: string): void {
  if (SUPPORTED_MIME_TYPES.has(mimeType) || mimeType.startsWith("text/")) {
    return;
  }
  throw new DocumentConvertRuntimeError(`Unsupported document type: ${mimeType}`, {
    code: "DOCUMENT_UNSUPPORTED_TYPE",
    status: 400,
  });
}

function resolveDocumentProxyEndpoint(documentProxyUrl: string): string {
  const trimmed = documentProxyUrl.replace(/\/$/, "");
  return trimmed.endsWith("/convert") ? trimmed : `${trimmed}${DEFAULT_DOCUMENT_PROXY_PATH}`;
}

function buildCacheKey(params: {
  appName: string;
  ownerUserId: string | null;
  corpus: string;
  sourceKey: string;
  checksum: string;
  provider: DocumentResolvedProvider;
}) {
  return buildDocumentCacheKey({
    appId: params.appName,
    ownerUserId: params.ownerUserId,
    corpus: params.corpus,
    sourceKey: params.sourceKey,
    checksum: params.checksum,
    provider: params.provider,
    artifact: "convert",
  });
}

function resolveFileExtension(filename: string): string {
  return filename.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] ?? "";
}

function isExplicitExternalProvider(input: DocumentConvertInput): boolean {
  const provider = input.provider;
  return Boolean(provider && provider !== "auto" && provider !== "local_text");
}

function shouldPreferPlatformAuto(input: DocumentConvertInput, mimeType: string, filename: string): boolean {
  const mode = input.mode ?? "auto";
  const provider = input.provider ?? "auto";
  if (mode !== "auto" || provider !== "auto") return false;
  return (
    PLATFORM_AUTO_MIME_TYPES.has(mimeType) || PLATFORM_AUTO_EXTENSIONS.has(resolveFileExtension(filename))
  );
}

function resolveExternalCandidateProviders(input: DocumentConvertInput): DocumentResolvedProvider[] {
  const mode = input.mode ?? "auto";
  const requestedProvider = input.provider;

  if (mode === "force-ocr") {
    if (requestedProvider && requestedProvider !== "auto" && requestedProvider !== "local_text") {
      return [requestedProvider];
    }
    return ["opendataloader", "openai", "gemini", "ocr", "custom_vlm"];
  }

  if (requestedProvider && requestedProvider !== "auto" && requestedProvider !== "local_text") {
    return [requestedProvider];
  }

  const providers: DocumentResolvedProvider[] = ["kordoc", "opendataloader"];
  if (mode === "force-external" || mode === "prefer-external" || input.paidFallback === true) {
    providers.push("gemini", "openai", "ocr", "custom_vlm");
  }
  return providers;
}

function resolveInitialCacheLookupProviders(
  input: DocumentConvertInput,
  mimeType: string,
  filename: string,
): DocumentResolvedProvider[] {
  const mode = input.mode ?? "auto";

  if (
    mode === "force-ocr" &&
    (!input.provider || input.provider === "auto" || input.provider === "local_text")
  ) {
    return [];
  }

  if (isExplicitExternalProvider(input)) {
    return resolveExternalCandidateProviders(input);
  }

  if (mode === "force-external" || mode === "force-ocr") {
    return resolveExternalCandidateProviders(input);
  }

  if (mode === "prefer-external") {
    return [...resolveExternalCandidateProviders(input), "local_text"];
  }

  if (shouldPreferPlatformAuto(input, mimeType, filename)) {
    return resolveExternalCandidateProviders(input);
  }

  return ["local_text"];
}

async function callExternalDocumentProxy(
  params: CreateDocumentConvertServiceOptions & {
    input: DocumentConvertInput;
    filename: string;
    mimeType: string;
    buffer: Buffer;
    checksum: string;
    ownerUserId: string | null;
  },
): Promise<DocumentConvertResult> {
  if (!params.documentProxyUrl) {
    throw new DocumentConvertRuntimeError("Document proxy is not configured", {
      code: "DOCUMENT_CONVERT_UNAVAILABLE",
      status: 503,
    });
  }

  let response: Response;
  try {
    response = await (params.fetchImpl ?? fetch)(resolveDocumentProxyEndpoint(params.documentProxyUrl), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(params.appToken ? { "X-App-Token": params.appToken } : {}),
      },
      body: JSON.stringify({
        appName: params.appName,
        workflowId: params.workflowId,
        workflowName: params.workflowName,
        input: {
          ...params.input,
          ownerUserId: params.ownerUserId ?? undefined,
          filename: params.filename,
          mimeType: params.mimeType,
        },
        file: {
          name: params.filename,
          type: params.mimeType,
          size: params.buffer.length,
          checksum: params.checksum,
          base64: params.buffer.toString("base64"),
        },
      }),
    });
  } catch (error) {
    throw new DocumentConvertRuntimeError(
      error instanceof Error ? error.message : "Document proxy request failed",
      {
        code: "DOCUMENT_CONVERT_UNAVAILABLE",
        status: 503,
      },
    );
  }

  if (!response.ok) {
    const contentType = response.headers.get("content-type") || "";
    let message = `Document proxy failed with status ${response.status}`;
    let code = response.status >= 500 ? "DOCUMENT_CONVERT_UNAVAILABLE" : "DOCUMENT_CONVERT_PROXY_FAILED";

    if (contentType.includes("application/json")) {
      const payload = (await response.json().catch(() => null)) as {
        error?: { message?: string; code?: string };
        message?: string;
        code?: string;
      } | null;
      message = payload?.error?.message?.trim() || payload?.message?.trim() || message;
      code = payload?.error?.code?.trim() || payload?.code?.trim() || code;
    } else {
      const body = await response.text().catch(() => "");
      if (body.trim()) {
        message = body.trim();
      }
    }

    throw new DocumentConvertRuntimeError(message, {
      code,
      status: response.status,
    });
  }

  return (await response.json()) as DocumentConvertResult;
}

export function createDocumentConvertService(
  options: CreateDocumentConvertServiceOptions,
): DocumentConvertService {
  return {
    async convert(input: DocumentConvertInput): Promise<DocumentConvertResult> {
      if (!options.workflowId) {
        throw new DocumentConvertRuntimeError("document.convert() is only available inside workflow()", {
          code: "DOCUMENT_CONVERT_WORKFLOW_ONLY",
          status: 400,
        });
      }

      if (!input.corpus?.trim()) {
        throw new DocumentConvertRuntimeError("corpus is required", {
          code: "DOCUMENT_CORPUS_REQUIRED",
          status: 400,
        });
      }

      const startedAt = (options.now ?? Date.now)();
      const stored = await options.storage.get(input.storageId);
      if (!stored) {
        throw new DocumentConvertRuntimeError(`storageId not found: ${input.storageId}`, {
          code: "DOCUMENT_STORAGE_NOT_FOUND",
          status: 404,
        });
      }

      if (stored.size > MAX_DOCUMENT_SIZE_BYTES) {
        throw new DocumentConvertRuntimeError("File too large for document conversion", {
          code: "DOCUMENT_TOO_LARGE",
          status: 413,
        });
      }

      const filename = input.filename?.trim() || stored.name;
      const mimeType = normalizeMimeType(input.mimeType || stored.type, filename);
      ensureSupportedMimeType(mimeType);

      const ownerUserId = resolveOwnerUserId(input, options.auth);
      const checksum = createHash("sha256").update(stored.buffer).digest("hex");
      const sourceKey = resolveDocumentSourceKey(input, filename);
      const mode = input.mode ?? "auto";
      const externalAllowed = await (options.resolveExternalPolicy
        ? options.resolveExternalPolicy({
            corpus: input.corpus,
            visibility: input.visibility,
            ownerUserId,
          })
        : resolveAllowExternalProvidersPolicy({
            corpus: input.corpus,
            visibility: input.visibility,
            ownerUserId,
            allowPrivateExternalCorpora: options.allowPrivateExternalCorpora,
          }));

      for (const cacheProvider of resolveInitialCacheLookupProviders(input, mimeType, filename)) {
        const cached = documentConvertCache.get(
          buildCacheKey({
            appName: options.appName,
            ownerUserId,
            corpus: input.corpus,
            sourceKey,
            checksum,
            provider: cacheProvider,
          }),
        );
        if (cached) {
          return cloneResult(cached);
        }
      }

      const requiresExternal =
        mode === "force-external" || mode === "force-ocr" || isExplicitExternalProvider(input);
      const prefersExternal =
        mode === "prefer-external" || shouldPreferPlatformAuto(input, mimeType, filename);
      let externalAttemptError: unknown = null;

      if ((requiresExternal || prefersExternal) && !externalAllowed) {
        if (requiresExternal) {
          throw new DocumentConvertRuntimeError(
            "external provider is required but denied for this private corpus",
            {
              code: "DOCUMENT_EXTERNAL_PROVIDER_REQUIRED",
              status: 422,
            },
          );
        }
      }

      if ((requiresExternal || prefersExternal) && externalAllowed) {
        try {
          const externalResult = await callExternalDocumentProxy({
            ...options,
            input,
            filename,
            mimeType,
            buffer: stored.buffer,
            checksum,
            ownerUserId,
          });
          documentConvertCache.set(
            buildCacheKey({
              appName: options.appName,
              ownerUserId,
              corpus: input.corpus,
              sourceKey,
              checksum,
              provider: externalResult.providerTrace.provider,
            }),
            cloneResult(externalResult),
          );
          return externalResult;
        } catch (error) {
          if (requiresExternal) {
            throw error;
          }
          externalAttemptError = error;
        }
      }

      let extracted: Awaited<ReturnType<typeof extractLocalDocument>>;
      try {
        extracted = await extractLocalDocument({
          filename,
          mimeType,
          buffer: stored.buffer,
        });
      } catch (error) {
        extracted = {
          text: "",
          markdown: "",
          pages: [],
          sections: [],
          warnings: [
            `local extraction unavailable: ${error instanceof Error ? error.message : String(error)}`,
          ],
        };
      }

      if (!extracted.text && !extracted.markdown) {
        if (mode === "text-only" || mode === "no-external-provider" || !externalAllowed) {
          throw new DocumentConvertRuntimeError("local extraction produced no usable text", {
            code: "DOCUMENT_EXTERNAL_PROVIDER_REQUIRED",
            status: 422,
          });
        }

        if (externalAttemptError) {
          throw externalAttemptError;
        }

        for (const cacheProvider of resolveExternalCandidateProviders(input)) {
          const cached = documentConvertCache.get(
            buildCacheKey({
              appName: options.appName,
              ownerUserId,
              corpus: input.corpus,
              sourceKey,
              checksum,
              provider: cacheProvider,
            }),
          );
          if (cached) {
            return cloneResult(cached);
          }
        }

        const externalResult = await callExternalDocumentProxy({
          ...options,
          input,
          filename,
          mimeType,
          buffer: stored.buffer,
          checksum,
          ownerUserId,
        });
        documentConvertCache.set(
          buildCacheKey({
            appName: options.appName,
            ownerUserId,
            corpus: input.corpus,
            sourceKey,
            checksum,
            provider: externalResult.providerTrace.provider,
          }),
          cloneResult(externalResult),
        );
        return externalResult;
      }

      const localResult: DocumentConvertResult = {
        text: extracted.text,
        markdown: extracted.markdown || extracted.text,
        pages: extracted.pages,
        sections: extracted.sections,
        warnings: extracted.warnings,
        providerTrace: {
          provider: "local_text",
          route: "local_text",
          external: false,
          durationMs: Math.max(0, (options.now ?? Date.now)() - startedAt),
          bytes: stored.size,
        },
      };

      documentConvertCache.set(
        buildCacheKey({
          appName: options.appName,
          ownerUserId,
          corpus: input.corpus,
          sourceKey,
          checksum,
          provider: "local_text",
        }),
        cloneResult(localResult),
      );
      return localResult;
    },
  };
}

export function createServerServices(params: {
  appName: string;
  storage: Storage;
  db?: RagCorpusPolicyDbLike;
  appToken?: string;
  documentProxyUrl?: string | null;
  allowPrivateExternalCorpora?: string[];
  fetchImpl?: typeof fetch;
  now?: () => number;
}): ServerServicesWithFactory {
  const resolveExternalPolicy: ResolveExternalPolicy = ({ corpus, visibility, ownerUserId }) =>
    resolveAllowExternalProvidersPolicy({
      db: params.db,
      corpus,
      visibility,
      ownerUserId,
      allowPrivateExternalCorpora: params.allowPrivateExternalCorpora,
    });

  return {
    [WORKFLOW_DOCUMENT_SERVICE_FACTORY]: ({ workflowId, workflowName, auth }) => ({
      document: createDocumentConvertService({
        workflowId,
        workflowName,
        appName: params.appName,
        storage: params.storage,
        auth,
        appToken: params.appToken,
        documentProxyUrl: params.documentProxyUrl,
        allowPrivateExternalCorpora: params.allowPrivateExternalCorpora,
        resolveExternalPolicy,
        fetchImpl: params.fetchImpl,
        now: params.now,
      }),
    }),
  };
}

export function attachWorkflowDocumentServices(
  services: GencowServicesCtx,
  params: { workflowId: string; workflowName: string; auth: AuthCtx },
): GencowServicesCtx & WorkflowDocumentServicesCtx {
  const factory = (services as ServerServicesWithFactory)[WORKFLOW_DOCUMENT_SERVICE_FACTORY];
  if (!factory) {
    return {
      ...services,
      document: createDocumentConvertService({
        workflowId: params.workflowId,
        workflowName: params.workflowName,
        appName: "unknown-app",
        storage: {
          get: async () => null,
          store: async () => "",
          storeBuffer: async () => "",
          getUrl: () => "",
          getPublicUrl: () => "",
          createReadGrant: async () => ({ url: "", expiresAt: new Date(0).toISOString() }),
          getMeta: async () => null,
          setVisibility: async () => {},
          listMeta: async () => ({ items: [], nextCursor: null }),
          delete: async () => {},
        },
        auth: params.auth,
      }),
    };
  }

  return {
    ...services,
    ...factory(params),
  };
}
